import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, Package, Tag, Users, LogOut, Cpu, ChevronRight, Home, ShoppingBag } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export type AdminSection = 'dashboard' | 'figurines' | 'categories' | 'users' | 'orders';

interface AdminSidebarProps {
  active: AdminSection;
  onChange: (s: AdminSection) => void;
}

const NAV = [
  { id: 'dashboard' as AdminSection, label: 'Dashboard',    icon: LayoutDashboard, color: 'var(--primary)' },
  { id: 'figurines' as AdminSection, label: 'Figurki',      icon: Package,         color: 'var(--accent)' },
  { id: 'orders'    as AdminSection, label: 'Zamówienia',   icon: ShoppingBag,     color: 'oklch(0.72 0.24 45)' },
  { id: 'categories'as AdminSection, label: 'Kategorie',    icon: Tag,             color: 'var(--chart-4)' },
  { id: 'users'     as AdminSection, label: 'Użytkownicy',  icon: Users,           color: 'var(--chart-3)' },
];

export default function AdminSidebar({ active, onChange }: AdminSidebarProps) {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();

  return (
    <aside
      className="flex flex-col w-64 min-h-screen border-r"
      style={{ background: 'oklch(0.10 0.022 280)', borderColor: 'oklch(0.22 0.04 280)' }}
    >
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-6 py-5 border-b" style={{ borderColor: 'oklch(0.22 0.04 280)' }}>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}
        >
          <Cpu className="w-4 h-4 text-white" />
        </div>
        <div>
          <div className="text-sm font-black tracking-widest text-foreground leading-none" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            SOKAO<span className="text-primary">-TOYS</span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            ADMIN PANEL
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-5 space-y-1">
        {NAV.map((item) => {
          const isActive = active === item.id;
          return (
            <motion.button
              key={item.id}
              onClick={() => onChange(item.id)}
              whileHover={{ x: 3 }}
              whileTap={{ scale: 0.97 }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 group"
              style={{
                fontFamily: 'Rajdhani, sans-serif',
                background: isActive ? `${item.color}18` : 'transparent',
                color: isActive ? item.color : 'var(--muted-foreground)',
                border: isActive ? `1px solid ${item.color}35` : '1px solid transparent',
              }}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left">{item.label}</span>
              {isActive && <ChevronRight className="w-3.5 h-3.5 opacity-60" />}
            </motion.button>
          );
        })}
      </nav>

      {/* User + Logout */}
      <div className="px-3 py-4 border-t space-y-2" style={{ borderColor: 'oklch(0.22 0.04 280)' }}>
        <div
          className="flex items-center gap-3 px-3 py-2 rounded-xl"
          style={{ background: 'oklch(0.16 0.03 280)' }}
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black text-white flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}
          >
            {(profile?.username?.[0] ?? 'A').toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-foreground truncate" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {profile?.username ?? 'Admin'}
            </div>
            <div className="text-xs text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              ADMINISTRATOR
            </div>
          </div>
        </div>
        <motion.button
          onClick={() => navigate('/')}
          whileHover={{ x: 2 }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-colors"
          style={{ fontFamily: 'Rajdhani, sans-serif', color: 'var(--accent)', background: 'oklch(0.68 0.28 195 / 0.08)', border: '1px solid oklch(0.68 0.28 195 / 0.25)' }}
        >
          <Home className="w-4 h-4" />
          Strona główna
        </motion.button>
        <motion.button
          onClick={signOut}
          whileHover={{ x: 2 }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-muted-foreground hover:text-destructive transition-colors"
          style={{ fontFamily: 'Rajdhani, sans-serif' }}
        >
          <LogOut className="w-4 h-4" />
          Wyloguj się
        </motion.button>
      </div>
    </aside>
  );
}
